import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { recurring as api, recurringAction, recurringGenerateNow, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Plus, Play, Pause, Ban, Zap, Trash2, Eye } from "lucide-react";

export default function Recurring() {
  const nav = useNavigate();
  const [list, setList] = useState(null);

  const load = () => api.list().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const doAction = async (id, action) => { try { await recurringAction(id, action); toast.success(`Schedule ${action}d`); load(); } catch (e) { toast.error(apiError(e)); } };
  const genNow = async (id) => { try { const inv = await recurringGenerateNow(id); toast.success(`Generated invoice ${inv.number}`); nav(`/invoices/${inv.id}`); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { try { await api.remove(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;

  return (
    <div>
      <PageHeader title="Recurring Invoices" subtitle="Automate repeat billing — invoices generate automatically on schedule.">
        <Button onClick={() => nav("/recurring/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="new-recurring-button"><Plus className="w-4 h-4 mr-1.5" /> New Recurring</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={RefreshCw} title="No recurring invoices" description="Create a template to automatically bill customers weekly, monthly, quarterly or yearly."
          action={<Button onClick={() => nav("/recurring/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-new-recurring"><Plus className="w-4 h-4 mr-1.5" /> New Recurring</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`recurring-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h3 className="font-display font-semibold text-[#0B132B] truncate">{r.title}</h3>
                  <p className="text-xs text-slate-500 truncate">{r.customer?.name} · <span className="capitalize">{r.frequency}</span></p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center bg-[#F1F5F9] rounded-lg py-3">
                <div><div className="font-display text-lg font-bold text-[#0B132B]">{money(r.line_items?.reduce((a, li) => a + (li.quantity * li.unit_price), 0) || 0)}</div><div className="text-[10px] text-slate-500 uppercase">Per invoice</div></div>
                <div><div className="font-display text-lg font-bold text-[#0B132B]">{r.generated_count || 0}</div><div className="text-[10px] text-slate-500 uppercase">Generated</div></div>
                <div><div className="font-display text-xs font-bold text-[#0B132B] mt-1.5">{r.status === "active" ? fmtDate(r.next_invoice_date) : "—"}</div><div className="text-[10px] text-slate-500 uppercase">Next</div></div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                <Button variant="outline" size="sm" onClick={() => nav(`/recurring/${r.id}`)} data-testid={`view-recurring-${r.id}`}><Eye className="w-4 h-4 mr-1" /> History</Button>
                {r.status === "active" && <Button variant="outline" size="sm" onClick={() => genNow(r.id)} data-testid={`generate-now-${r.id}`}><Zap className="w-4 h-4 mr-1" /> Generate</Button>}
                {r.status === "active" && <Button variant="outline" size="sm" onClick={() => doAction(r.id, "pause")} data-testid={`pause-${r.id}`}><Pause className="w-4 h-4 mr-1" /> Pause</Button>}
                {r.status === "paused" && <Button variant="outline" size="sm" onClick={() => doAction(r.id, "resume")} data-testid={`resume-${r.id}`}><Play className="w-4 h-4 mr-1" /> Resume</Button>}
                {r.status !== "cancelled" && <Button variant="outline" size="sm" className="text-slate-600" onClick={() => doAction(r.id, "cancel")} data-testid={`cancel-recurring-${r.id}`}><Ban className="w-4 h-4 mr-1" /> Cancel</Button>}
                <ConfirmDelete onConfirm={() => remove(r.id)} title="Delete recurring schedule?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-recurring-${r.id}`}><Trash2 className="w-4 h-4" /></Button>} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
